import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { Download, Copy, Check, Maximize2, X } from 'lucide-react';
import { toast } from 'sonner';
import { createPortal } from 'react-dom';
import { useLanguage } from '../context/LanguageContext';

// Initialize mermaid with dark theme
mermaid.initialize({
  startOnLoad: false,
  theme: 'dark',
  securityLevel: 'loose',
  fontFamily: 'ui-monospace, monospace',
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis'
  },
  sequence: {
    useMaxWidth: true,
    diagramMarginX: 8,
    diagramMarginY: 8
  }
});

// Floating diagram viewer
const FloatingDiagramViewer = ({ svgContent, title, onClose, t }) => {
  const [position, setPosition] = useState({ x: 150, y: 100 });
  const [size, setSize] = useState({ width: 600, height: 500 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    if (e.target.closest('.resize-handle')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleResizeStart = (e) => {
    e.stopPropagation();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        setPosition({
          x: Math.max(0, e.clientX - dragOffset.x),
          y: Math.max(0, e.clientY - dragOffset.y)
        });
      }
      if (isResizing) {
        setSize({
          width: Math.max(300, e.clientX - position.x),
          height: Math.max(200, e.clientY - position.y)
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, position]);

  const handleDownload = () => {
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `diagram_${Date.now()}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(t.diagramDownloaded);
  };

  return createPortal(
    <div
      className="fixed z-[10000] bg-zinc-900 border border-zinc-600 rounded-xl shadow-2xl overflow-hidden"
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height
      }}
    >
      <div
        className="flex items-center justify-between px-3 py-2 bg-zinc-800 cursor-grab active:cursor-grabbing select-none"
        onMouseDown={handleMouseDown}
      >
        <span className="text-xs text-zinc-300 truncate max-w-[200px]">{title}</span>
        <div className="flex items-center gap-1">
          <button onClick={handleDownload} className="p-1 hover:bg-zinc-700 rounded" title={t.downloadSvg}>
            <Download className="w-4 h-4 text-green-400" />
          </button>
          <button onClick={onClose} className="p-1 hover:bg-zinc-700 rounded" title={t.close}>
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        </div>
      </div>
      <div 
        className="w-full overflow-auto bg-zinc-800 p-4"
        style={{ height: size.height - 40 }}
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />
      <div
        className="resize-handle absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
        onMouseDown={handleResizeStart}
      >
        <svg className="w-4 h-4 text-zinc-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z" />
        </svg>
      </div>
    </div>,
    document.body
  );
};

const MermaidDiagram = ({ code, title }) => {
  const { language } = useLanguage();
  const containerRef = useRef(null);
  const [svg, setSvg] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isFloating, setIsFloating] = useState(false);

  const txt = {
    es: {
      diagram: 'Diagrama',
      diagramDownloaded: 'Diagrama descargado',
      codeCopied: 'Código del diagrama copiado',
      copyError: 'Error al copiar',
      downloadedSvg: 'Diagrama descargado como SVG',
      diagramError: 'Error en el diagrama',
      copyCode: 'Copiar código',
      downloadSvg: 'Descargar SVG',
      openFloating: 'Abrir en ventana flotante',
      close: 'Cerrar',
      renderError: 'Error al renderizar el diagrama'
    },
    en: {
      diagram: 'Diagram',
      diagramDownloaded: 'Diagram downloaded',
      codeCopied: 'Diagram code copied',
      copyError: 'Copy error',
      downloadedSvg: 'Diagram downloaded as SVG',
      diagramError: 'Diagram error',
      copyCode: 'Copy code',
      downloadSvg: 'Download SVG',
      openFloating: 'Open in floating window',
      close: 'Close',
      renderError: 'Error rendering diagram'
    }
  };
  const t = txt[language] || txt.es;
  const displayTitle = title || t.diagram;

  useEffect(() => {
    const renderDiagram = async () => {
      if (!code || !containerRef.current) return;

      try {
        // Generate unique ID for the diagram
        const id = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Render the diagram
        const { svg: renderedSvg } = await mermaid.render(id, code);
        setSvg(renderedSvg);
        setError(null);
      } catch (err) {
        console.error('Mermaid render error:', err);
        setError(err.message || t.renderError);
        setSvg('');
      }
    };

    renderDiagram();
  }, [code, t.renderError]);

  const handleCopy = async () => {
    // Helper function for textarea fallback
    const copyWithTextarea = () => {
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.cssText = 'position:fixed;left:-999999px;top:-999999px;opacity:0;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    };

    // Try modern clipboard API first
    let success = false;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(code);
        success = true;
      }
    } catch (clipErr) {
    }

    // Fallback to execCommand
    if (!success) {
      success = copyWithTextarea();
    }

    if (success) {
      setCopied(true);
      toast.success(t.codeCopied);
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error(t.copyError);
    }
  };

  const handleDownload = () => {
    if (!svg) return;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `diagram_${Date.now()}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(t.downloadedSvg);
  };

  if (error) {
    // 🟥 CUADRO NEGRO P1 — UI de error sutil. Mermaid v11.x lanza
    // "Syntax error in text" sobre diagramas legacy; antes esto mostraba
    // un bloque rojo grande con el código fuente expuesto. Ahora mostramos
    // un placeholder discreto + un <details> opcional para ver el código.
    return (
      <div
        className="my-3 p-3 rounded-lg border border-rose-500/30 bg-rose-950/20 light:bg-rose-50 light:border-rose-200"
        data-testid="mermaid-render-error"
      >
        <p className="text-rose-300 light:text-rose-700 text-sm font-medium">
          {t.diagramError || 'No se pudo renderizar el diagrama Mermaid.'}
        </p>
        <details className="mt-1">
          <summary className="text-xs text-rose-400 light:text-rose-600 cursor-pointer select-none opacity-70 hover:opacity-100">
            {t.copyCode ? `${t.copyCode}` : 'Ver código'}
          </summary>
          <pre className="mt-2 p-2 bg-zinc-900/60 light:bg-zinc-100 rounded text-xs text-zinc-400 light:text-zinc-600 overflow-x-auto">
            {code}
          </pre>
        </details>
      </div>
    );
  }

  return (
    <>
      <div className="my-3 bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 bg-zinc-800 border-b border-zinc-700">
          <span className="text-xs text-zinc-400 font-medium">📊 {displayTitle}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={handleCopy}
              className="p-1 hover:bg-zinc-700 rounded transition-colors"
              title={t.copyCode}
            >
              {copied ? (
                <Check className="w-3.5 h-3.5 text-green-400" />
              ) : (
                <Copy className="w-3.5 h-3.5 text-zinc-400" />
              )}
            </button>
            <button
              onClick={handleDownload}
              className="p-1 hover:bg-zinc-700 rounded transition-colors"
              title={t.downloadSvg}
            >
              <Download className="w-3.5 h-3.5 text-zinc-400" />
            </button>
            <button
              onClick={() => setIsFloating(true)}
              className="p-1 hover:bg-zinc-700 rounded transition-colors"
              title={t.openFloating}
            >
              <Maximize2 className="w-3.5 h-3.5 text-zinc-400" />
            </button>
          </div>
        </div>

        {/* Diagram */}
        <div
          ref={containerRef}
          className="p-4 overflow-x-auto flex justify-center items-center min-h-[100px] [&_svg]:max-w-full"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>

      {/* Floating viewer */}
      {isFloating && svg && (
        <FloatingDiagramViewer
          svgContent={svg}
          title={displayTitle}
          onClose={() => setIsFloating(false)}
          t={t}
        />
      )}
    </>
  );
};

export default MermaidDiagram;
